import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import PlannedActionEditDrawer from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/PlannedActionEditDrawer';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useToggle } from 'react-use';
import { PlannedAction } from 'types/iapBaseTypes';

const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const PlannedActionsTable = ({
  statusSummaryId,
  originalPlannedActions = [],
}: {
  statusSummaryId: string;
  originalPlannedActions?: PlannedAction[];
}) => {
  const [editOpen, toggleEditDrawer] = useToggle(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAction, setSelectedAction] = useState<
    (PlannedAction & { id: string }) | null
  >(null);
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const form = useForm({
    mode: 'onChange',
    defaultValues: { plannedActions: originalPlannedActions },
  });

  const { control, register, getValues } = form;

  const { append, remove, update, fields } = useFieldArray<{
    plannedActions: PlannedAction[];
  }>({
    control,
    name: 'plannedActions',
  });

  const rows = useMemo(() => {
    const filteredFields = fields.filter((field) =>
      field.task.toLowerCase().includes(search.toLowerCase())
    );

    const finalField = fields[fields.length - 1];

    if (
      finalField &&
      !filteredFields.some((field) => field.id === finalField.id) &&
      selectedAction &&
      selectedAction.id === APPENDED_ROW_ID
    ) {
      return filteredFields.concat(finalField);
    }

    return filteredFields;
  }, [fields, search, selectedAction]);

  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    return rows.length === selectedIds.length;
  }, [rows, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(allSelected ? [] : rows.map((field) => field.id));
  }, [allSelected, rows]);

  const toggleRow = useCallback(
    (id: string) => {
      const set = new Set(selectedIds);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      setSelectedIds([...set]);
    },
    [selectedIds]
  );

  const deleteSelected = useCallback(() => {
    const toDeleteIndexes = fields.reduce<number[]>((acc, field, index) => {
      if (selectedIds.includes(field.id)) {
        acc.push(index);
      }
      return acc;
    }, []);

    upsertIncidentStatusSummary({
      plannedActions: getValues('plannedActions').filter(
        (_, index) => !toDeleteIndexes.includes(index)
      ),
    });
    remove(toDeleteIndexes);
    setSelectedIds([]);
  }, [
    remove,
    fields,
    selectedIds,
    getValues,
    upsertIncidentStatusSummary,
    setSelectedIds,
  ]);

  const deleteRow = useCallback(
    (rowToDelete: PlannedAction & { id: string }) => {
      const index = fields.findIndex((field) => field.id === rowToDelete.id);
      if (index !== -1) {
        remove(index);
      }
      setSelectedIds((prev) => prev.filter((x) => x !== rowToDelete.id));
      upsertIncidentStatusSummary({
        plannedActions: getValues('plannedActions'),
      });
    },
    [remove, fields, getValues, upsertIncidentStatusSummary, setSelectedIds]
  );

  const appendBlankPlannedAction = useCallback(() => {
    const blankPlannedAction = {
      id: APPENDED_ROW_ID,
      task: '',
      pointOfContact: '',
      pocBriefed: false,
      startDate: null,
      deadline: null,
      status: null,
    };

    append(blankPlannedAction);
    setSelectedAction(blankPlannedAction);
    toggleEditDrawer(true);
  }, [append, setSelectedAction, toggleEditDrawer]);

  const handleSavePlannedAction = useCallback(
    (updatedAction: PlannedAction) => {
      if (!selectedAction) return;

      if (selectedAction.id === APPENDED_ROW_ID) {
        update(fields.length - 1, { ...updatedAction });
      } else {
        const index = fields.findIndex(
          (field) => field.id === selectedAction.id
        );
        if (index !== -1) {
          update(index, { ...updatedAction });
        }
      }

      setSelectedAction(null);
      toggleEditDrawer(false);
      upsertIncidentStatusSummary({
        plannedActions: getValues('plannedActions'),
      });
    },
    [
      selectedAction,
      fields,
      update,
      toggleEditDrawer,
      upsertIncidentStatusSummary,
      getValues,
    ]
  );

  const closePlannedActionDrawer = useCallback(() => {
    // useFieldArray replaces each row's `id` with RHF's internal key, so we cannot
    // find the draft row by APPENDED_ROW_ID. Add flow always appends at the end.
    if (selectedAction?.id === APPENDED_ROW_ID && fields.length > 0) {
      remove(fields.length - 1);
    }
    setSelectedAction(null);
    toggleEditDrawer(false);
  }, [selectedAction, fields, remove, toggleEditDrawer]);

  const columns = useMemo<ColumnDef<PlannedAction & { id: string }>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <StylishNewCheckbox
            checked={allSelected}
            onChange={toggleSelectAll}
            disabled={rows.length === 0 || selectedAction !== null}
          />
        ),
        cell: ({ row }) => (
          <StylishNewCheckbox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => {
              toggleRow(row.original.id);
            }}
            disabled={selectedAction !== null}
          />
        ),
        size: 10,
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'task',
        header: () => headerCell('Task'),
        cell: ({ getValue }) => {
          return <span>{getValue<string>() || ''}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'pointOfContact',
        header: () => headerCell('Point of Contact'),
        cell: ({ getValue }) => {
          return <span>{getValue<string>() || ''}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'pocBriefed',
        header: () => headerCell('POC Briefed'),
        cell: ({ row }) => (
          <StylishNewCheckbox
            {...register(`plannedActions.${row.index}.pocBriefed`)}
            disabled={true}
          />
        ),
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'startDate',
        header: () => headerCell('Start Date'),
        cell: ({ getValue }) => {
          const value = getValue<Date | null>();
          const text = value ? new Date(value).toLocaleDateString() : '';
          return <span>{text}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'deadline',
        header: () => headerCell('Deadline'),
        cell: ({ getValue }) => {
          const value = getValue<Date | null>();
          const text = value ? new Date(value).toLocaleDateString() : '';
          return <span>{text}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'status',
        header: () => headerCell('Status'),
        cell: ({ getValue }) => {
          return <span>{getValue<string>() || ''}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <StylishNewButton
              className="button--secondary border-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAction(row.original);
                toggleEditDrawer(true);
              }}
            >
              <SharedIcon iconName={'edit'} classes="me-1" />
            </StylishNewButton>
            <StylishNewButton
              className="button--secondary border-0"
              onClick={(e) => {
                e.stopPropagation();
                deleteRow(row.original);
              }}
            >
              <SharedIcon iconName="delete" classes="me-1" />
            </StylishNewButton>
          </div>
        ),
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'left' },
      },
    ],
    [
      allSelected,
      selectedIds,
      toggleRow,
      toggleSelectAll,
      register,
      deleteRow,
      toggleEditDrawer,
      selectedAction,
      rows.length,
    ]
  );

  return (
    <>
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <div className="d-flex justify-content-between align-items-end mb-2 gap-2 flex-wrap">
            <div
              className="flex-grow-1 justify-content-between align-items-center gap-2 d-flex"
              style={{ minWidth: 240 }}
            >
              <label style={{ whiteSpace: 'nowrap' }}>
                Planned Actions for Next Operational Period
              </label>
              <input
                className="form-control"
                placeholder="Search planned actions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ paddingLeft: '4rem' }}>
              <StylishNewButton
                className="button button--primary button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  appendBlankPlannedAction();
                }}
                disabled={selectedAction !== null}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Planned Action
              </StylishNewButton>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>{selectedIds.length} selected</div>
              <StylishNewButton
                className="button--error button--sml"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSelected();
                }}
                disabled={selectedAction !== null}
              >
                <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
              </StylishNewButton>
            </div>
          )}
          <PratusTable
            className="layout-auto pratus-table"
            columns={columns}
            data={rows}
            getRowId={(row) => row.id}
            noDataIndication={
              <div className="text-center p-2">
                {search
                  ? 'No results!'
                  : 'There are no planned actions yet. Click + Add Planned Action to add a planned action.'}
              </div>
            }
            footer={
              <span className="text-nowrap me-2">{`${rows.length} planned actions`}</span>
            }
          />
          {editOpen && selectedAction ? (
            <PlannedActionEditDrawer
              isNewAction={selectedAction.id === APPENDED_ROW_ID}
              selectedAction={selectedAction}
              onDismiss={closePlannedActionDrawer}
              onSave={handleSavePlannedAction}
            />
          ) : null}
        </div>
      </FormProvider>
    </>
  );
};

export default PlannedActionsTable;
